import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { StageTracker } from "../components/StageTracker";
import { useSettings } from "../context/SettingsContext";
import { FAQ, PRIORITY_FACTORS, HOLIDAY_FAIR } from "../lib/content";

export default function Program() {
  const { settings } = useSettings();
  const stage = settings?.stage ?? 2;
  const censusUrl = settings?.census_url;
  const [openFaq, setOpenFaq] = useState(-1);

  return (
    <>
      <PageHero
        eyebrow="A Kamalama Foundation program"
        lines={[
          { text: "Priced Out of" },
          { text: "Kauaʻi by Lowell", className: "font-serifg italic font-semibold" },
        ]}
        lead="Supporting the local businesses that help make our island home."
      />

      <section className="wrap py-16 sm:py-20">
        <Reveal className="flex max-w-3xl flex-col gap-4">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Program overview
          </h2>
          <p className="leading-relaxed text-muted-foreground">
            Priced Out of Kauaʻi by Lowell is a Hurricane Lowell
            emergency-relief and recovery program for Hawaiʻi residents who
            live on Kauaʻi and own and operate locally owned businesses
            physically located on Kauaʻi. It is a program of Kamalama
            Foundation, not a separate nonprofit.
          </p>
          <p className="leading-relaxed text-muted-foreground">
            Hurricane Lowell, and the drop in visitors that followed, hit the
            small, locally owned businesses that depend on steady customers
            hardest. When income stops, business rent and household rent are
            often the first bills that put an owner at risk of closing or
            losing their home.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <StageTracker stage={stage} />
        </Reveal>
      </section>

      <section className="border-t border-border bg-muted/40">
        <div className="wrap grid gap-10 py-16 sm:py-20 lg:grid-cols-2">
          <Reveal className="flex flex-col items-start gap-4">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Who the program is meant for
            </h2>
            <p className="text-muted-foreground">
              Our current intended eligibility focus:
            </p>
            <ul className="flex flex-col gap-2.5">
              {[
                "Business is physically located on Kauaʻi",
                "Business is locally owned and operated",
                "Owner currently lives on Kauaʻi and is a Hawaiʻi resident",
                "Owner holds a current Hawaiʻi driver's license or Hawaiʻi State ID",
                "Business was affected by Hurricane Lowell or related tourism disruption",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span
                    className="mt-[9px] h-2 w-2 shrink-0 rounded-full bg-terra"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">
              The Board will approve and publish final eligibility
              requirements before any awards are made.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col gap-4">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Planned rent-first recovery
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              If the Board approves an assistance program and funds allow, the
              first priority is helping verified eligible owners stabilize:
            </p>
            <ol className="flex list-decimal flex-col gap-2 pl-5">
              <li>
                One month of essential{" "}
                <b>business rent or lease</b>; and
              </li>
              <li>
                One month of essential{" "}
                <b>household rent or mortgage</b>, where Lowell-related
                business-income loss has created documented unmet housing need.
              </li>
            </ol>
            <p className="leading-relaxed text-muted-foreground">
              Depending on funds and the number of qualified applicants, this
              could be full or partial assistance, a capped base award, or a
              priority-based award. If funds remain, the Board may approve
              supplemental help with verified essential needs such as
              utilities, insurance, inventory, repairs, equipment, payroll,
              food, transportation, childcare, or medical costs.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="wrap py-16 sm:py-20">
        <Reveal className="flex max-w-3xl flex-col gap-4">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            How future assistance will be decided
          </h2>
          <p className="leading-relaxed text-muted-foreground">
            Limited funds call for a clear, published process. Before any
            application is reviewed, the Board will adopt and publish a
            written priority framework.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          <Reveal className="h-full">
            <div className="card-surface h-full">
              <p className="eyebrow">Priority factors</p>
              <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
                {PRIORITY_FACTORS.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span
                      className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-terra"
                      aria-hidden="true"
                    />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="h-full">
            <div className="card-surface h-full">
              <p className="eyebrow">Who decides</p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Staff and trained volunteers may organize applications and
                request missing information, but they do not make award
                decisions.
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Final awards are approved by the Board of Directors or by an{" "}
                <b className="text-foreground">Independent Grant Review
                Committee</b> of at least three people with no personal,
                family, financial or business interest in the applications
                they review.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.16} className="h-full">
            <div className="card-surface h-full">
              <p className="eyebrow">Conflict safeguards</p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                If a founder, director, officer, staff member, volunteer with
                program authority, family member, or related business applies,
                that person must disclose the interest and fully recuse from
                rule-making, review, decisions, and payment for that
                application.
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-8">
          <div className="callout border-terra/40 bg-terra/5">
            <b className="font-display">What we can and can't promise</b>
            <p className="text-muted-foreground">
              The Foundation cannot promise an award, a particular amount,
              full rent payment, or reimbursement of business losses. Any
              assistance depends on available charitable funds,
              Board-approved criteria, verification, insurance and other aid
              received, the number of qualified applicants, and award caps.
            </p>
          </div>
        </Reveal>
      </section>

      <section
        className="border-t border-border bg-muted/40"
        id="holiday-fair"
        data-testid="holiday-fair-section"
      >
        <div className="wrap flex max-w-3xl flex-col gap-4 py-16 sm:py-20">
          <Reveal className="flex flex-col items-start gap-4">
            <p className="eyebrow">Fundraising, grants & events</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Kamalama Holiday Fair
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              One day of holiday cheer for the whole ʻohana:{" "}
              <b className="text-foreground">{HOLIDAY_FAIR.date}</b>,{" "}
              {HOLIDAY_FAIR.hours}. Local vendors and makers, ʻohana food,
              keiki activities, and community — Kamalama Foundation is
              producing this fair, and net proceeds after disclosed event
              expenses support our charitable programs, including the Priced
              Out of Kauaʻi by Lowell emergency-relief and recovery program.
            </p>
            <p className="text-sm text-muted-foreground">
              Venue, vendor fees, and the full lineup will be announced on the
              event page.
            </p>
            <Link
              to="/holiday-fair"
              className="btn"
              data-testid="holiday-fair-details-link"
            >
              Event details & vendor sign-up
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="wrap py-16 sm:py-20" data-testid="faq-section">
        <Reveal>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Frequently asked questions
          </h2>
        </Reveal>
        <Reveal delay={0.08} className="mt-8">
          <div>
            {FAQ.map(([q, a], i) => (
              <div className="faq-item" key={i}>
                <button
                  className="faq-trigger"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  data-testid={`faq-trigger-${i}`}
                >
                  <span className="max-w-[52rem]">{q}</span>
                  <span className="font-mono text-2xl text-terra" aria-hidden="true">
                    {openFaq === i ? "−" : "+"}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="faq-answer"
                    >
                      <p className="max-w-3xl leading-relaxed">{a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </>
  );
}
